import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import ChatEmpty from "./ChatEmpty";
import MessageBubble from "./MessageBubble/MessageBubble";
import { formatDate } from "src/utils/DateTimeFormatter";
import MessageComposer from "./MessageComposer/MessageComposer";
import WindowClosed from "./WindowClosed";
import TemplateSend from "./TemplateSend";
import "../style/style.css"

const NEAR_BOTTOM_PX = 140;
const NEAR_TOP_PX = 160;

function MessageList({ loading, messages = [], hasOlder = false, loadingOlder = false, olderError = "", onLoadOlder, onReply, conversation, replyTo, onCancelReply, onMessageSent, onTemplateClick, is24HourWindowOpen, showTemplateSend, onCloseTemplate, onTemplateSent, onSendTemplate, contactName }) {

  const messageListRef = useRef(null);
  const atBottomRef = useRef(true);
  const previousMessageIds = useRef(new Set());
  const initializedConversation = useRef(null);
  // Where the reader was before earlier messages were added above them.
  const olderAnchor = useRef(null);
  const [showJump, setShowJump] = useState(false);

  const requestOlder = useCallback(() => {
    const container = messageListRef.current;
    if (!container || !hasOlder || loadingOlder || !onLoadOlder) return;
    olderAnchor.current = { firstId: messages[0]?.id, height: container.scrollHeight, top: container.scrollTop };
    onLoadOlder();
  }, [hasOlder, loadingOlder, onLoadOlder, messages]);

  // Keep the same message under the reader's eyes when earlier ones are prepended.
  useLayoutEffect(() => {
    const anchor = olderAnchor.current;
    const container = messageListRef.current;
    if (!anchor || !container) return;
    if (messages[0]?.id !== anchor.firstId) {
      container.scrollTop = container.scrollHeight - anchor.height + anchor.top;
      olderAnchor.current = null;
    } else if (!loadingOlder) {
      olderAnchor.current = null;
    }
  }, [messages, loadingOlder]);

  const scrollToBottom = useCallback((behavior = "auto") => {
    requestAnimationFrame(() => {
      const container = messageListRef.current;
      if (!container) return;
      container.scrollTo({ top: container.scrollHeight, behavior });
      atBottomRef.current = true;
      setShowJump(false);
    });
  }, []);

  const handleScroll = useCallback(() => {
    const container = messageListRef.current;
    if (!container) return;
    const distance =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    atBottomRef.current = distance < NEAR_BOTTOM_PX;
    if (atBottomRef.current) setShowJump(false);
    if (container.scrollTop < NEAR_TOP_PX && initializedConversation.current === conversation?.id) requestOlder();
  }, [requestOlder, conversation?.id]);

  // Initialize the reading position once per conversation, never per refresh.
  useEffect(() => {
    initializedConversation.current = null;
    previousMessageIds.current = new Set();
    atBottomRef.current = true;
    setShowJump(false);
  }, [conversation?.id]);

  useEffect(() => {
    if (loading || initializedConversation.current === conversation?.id) return;
    initializedConversation.current = conversation?.id;
    previousMessageIds.current = new Set(messages.map((message) => message.id));
    if (messages.length) scrollToBottom("auto");
  }, [loading, conversation?.id, messages, scrollToBottom]);

  // New messages: follow only if the reader is already near the bottom or the
  // new message is their own; otherwise offer a jump-to-latest pill.
  useEffect(() => {
    if (loading) return;
    const last = messages[messages.length - 1];
    const arrived = last && !previousMessageIds.current.has(last.id);
    previousMessageIds.current = new Set(messages.map((message) => message.id));
    if (!arrived) return;

    // The API returns a bounded recent window. A new message can replace an
    // older row without increasing the count, so compare the newest identity.
    const mine = last?.direction === "Outgoing";

    if (atBottomRef.current || mine) {
      scrollToBottom(mine ? "smooth" : "auto");
    } else {
      setShowJump(true);
    }
  }, [messages, loading, scrollToBottom]);


  const formatDateKey = (dateValue) => {
    if (!dateValue) return null;
    
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString().split("T")[0];
  };

  
 return (
  <div
    ref={messageListRef}
    onScroll={handleScroll}
    className="wa-thread message-list-bg"
  >
    {showJump && (
      <button
        type="button"
        onClick={() => scrollToBottom("smooth")}
        className="wa-jump"
        title="Jump to latest"
        aria-label="Jump to latest"
      >
        <ChevronDown size={16} />New messages
      </button>
    )}
    <div className="wa-thread-inner">
      <div className="wa-thread-messages">
        {loading ? (
          <div className="wa-thread-state">
            <span className="wa-spinner" aria-hidden="true" />
            <span>Loading messages…</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full min-h-[200px] items-center justify-center">
            <ChatEmpty />
          </div>
        ) : (
          <>
          {(hasOlder || loadingOlder || olderError) && (
            <div className="wa-day wa-older">
              {loadingOlder ? (
                <span role="status"><span className="wa-spinner" aria-hidden="true" />Loading earlier messages…</span>
              ) : (
                <button type="button" onClick={requestOlder}>
                  {olderError ? `${olderError} Retry` : "Load earlier messages"}
                </button>
              )}
            </div>
          )}
          {messages.map((message, index) => {
            const messageDate = message.meta_timestamp ?? message.created_at;
            const currentDateKey = formatDateKey(messageDate);
            const previousMessage = messages[index - 1];
            const previousDate = previousMessage?.meta_timestamp ?? previousMessage?.created_at;
            const previousDateKey = formatDateKey(previousDate);
            const shouldShowDate = index === 0 || currentDateKey !== previousDateKey;
            return (
              <React.Fragment key={message.id}>
                {shouldShowDate && (
                  <div className="wa-day"><span>{formatDate(messageDate)}</span></div>
                )}
                <div className="min-w-0">
                  <MessageBubble message={message} onReply={onReply}
                  />
                </div>
              </React.Fragment>
            );
          })}
          </>
        )}
      </div>
      <div className="wa-dock">
        {showTemplateSend ? (
          <TemplateSend
            conversation={conversation}
            onMessageSent={onTemplateSent}
            onClose={onCloseTemplate}
            onSendTemplate={onSendTemplate}
          />
        ) : !is24HourWindowOpen ? (
          <WindowClosed name={contactName} onTemplateClick={onTemplateClick} neverWrote={!messages.some((message) => message.direction === "Incoming")} />
        ) : (
          <MessageComposer
            conversation={conversation}
            onMessageSent={onMessageSent}
            replyTo={replyTo}
            onCancelReply={onCancelReply}
            onTemplateClick={onTemplateClick}
          />
        )}
      </div>
    </div>
  </div>
);
}

export default MessageList;
